import { FlaskConical, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { RohsTestReportAuditStoredEntry } from '@/shared/hooks/api/rohsComplianceFiles'
import { cn } from '@/shared/lib/utils'
import {
  Attachment,
  AttachmentContent,
  AttachmentDescription,
  AttachmentMedia,
  AttachmentTitle,
} from '@/components/ui/attachment'
import { Badge } from '@/components/ui/badge'
import {
  auditResultCardClassName,
  auditResultMediaClassName,
  formatAuditTimestamp,
  isRohsTestReportAuditPass,
  verdictBadgeVariant,
} from './rohsTestReportAuditDisplay'

export function V2ComponentRohsTestReportAuditSummaryCard({
  latest,
  isRunning,
  locale,
  onOpen,
  interactive = true,
  className,
}: {
  latest: RohsTestReportAuditStoredEntry | null | undefined
  isRunning: boolean
  locale: string
  onOpen?: () => void
  interactive?: boolean
  className?: string
}) {
  const { t } = useTranslation('common')
  const baseKey = 'v2.pages.components.detail.rohsBriefing.testReportAudit'

  const empty = !isRunning && latest == null
  let attachmentState: 'done' | 'processing' | 'idle' | 'error' = 'done'
  if (isRunning) {
    attachmentState = 'processing'
  } else if (empty) {
    attachmentState = 'idle'
  } else if (
    latest != null &&
    verdictBadgeVariant(latest.verdict) === 'danger'
  ) {
    attachmentState = 'error'
  }

  let description = t(`${baseKey}.viewAudit`)
  if (isRunning) {
    description = t(`${baseKey}.running`)
  } else if (latest != null) {
    description = formatAuditTimestamp(latest.timestamp, locale)
  } else {
    description = t(`${baseKey}.summaryNone`)
  }

  const card = (
    <Attachment
      state={attachmentState}
      size='sm'
      className={cn(
        'w-full',
        interactive ? 'cursor-pointer' : undefined,
        auditResultCardClassName(latest?.verdict ?? null, {
          running: isRunning,
          empty,
        })
      )}
    >
      <AttachmentMedia
        className={auditResultMediaClassName(latest?.verdict ?? null, {
          running: isRunning,
          empty,
        })}
      >
        {isRunning ? (
          <Loader2 className='size-4 animate-spin' aria-hidden />
        ) : (
          <FlaskConical aria-hidden />
        )}
      </AttachmentMedia>
      <AttachmentContent>
        <div className='flex min-w-0 items-center gap-2'>
          <AttachmentTitle title={t(`${baseKey}.title`)}>
            {t(`${baseKey}.title`)}
          </AttachmentTitle>
          {isRunning ? (
            <Badge variant='secondary' className='shrink-0'>
              {t(`${baseKey}.running`)}
            </Badge>
          ) : null}
          {!isRunning && latest != null ? (
            <Badge
              variant={verdictBadgeVariant(latest.verdict)}
              className='shrink-0'
            >
              {isRohsTestReportAuditPass(latest.verdict)
                ? t(`${baseKey}.summaryPass`)
                : t(`${baseKey}.summaryFail`)}
            </Badge>
          ) : null}
        </div>
        <AttachmentDescription>{description}</AttachmentDescription>
      </AttachmentContent>
    </Attachment>
  )

  if (!interactive) {
    return <div className={cn('w-full', className)}>{card}</div>
  }

  return (
    <button
      type='button'
      className={cn(
        'w-full rounded-xl text-start transition-opacity hover:opacity-95 focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:outline-none',
        className
      )}
      onClick={onOpen}
    >
      {card}
    </button>
  )
}
